import { provide, inject, computed, type InjectionKey, type ComputedRef, type Ref } from 'vue';
import type { BaseGameState } from '../../types/index.js';
import { gameMap } from '../../games/registry.js';
import { useGameStore } from '../stores/game.js';
import { useToast, type ToastContext } from './useToast.js';

export interface GameContext<S extends BaseGameState = BaseGameState> {
  state: ComputedRef<S>;
  /** Boardgame.io context (phase, currentPlayer, etc.). */
  ctx: Ref<{
    currentPlayer: string;
    phase?: string | null;
    gameover?: unknown;
    activePlayers?: Record<string, string> | null;
  }>;
  currentPlayer: Ref<string>;
  playerID: Ref<string | null>;
  matchID: Ref<string | null>;
  gameId: Ref<string | null>;
  gameover: Ref<{ winner?: string; isDraw?: boolean } | undefined>;
  isActive: Ref<boolean>;
  isConnected: Ref<boolean>;
  /**
   * True while rejoining with stored credentials after a refresh.
   * Cleared after the first synced state, or after a timeout if the socket never connects
   * (so the UI can show generic “connecting / check server URL” copy).
   */
  reconnecting: Ref<boolean>;
  isMyTurn: ComputedRef<boolean>;
  /** True when undo is likely allowed (local: `_undo` stack; multiplayer: `numMoves` proxy — server strips `_undo`). */
  canUndo: ComputedRef<boolean>;
  /** Rewind the last undoable move (server-authoritative in multiplayer). */
  undo: () => void;
  /** Dispatch a boardgame.io move by name. Shows a toast if the move is invalid. */
  move: (name: string, ...args: unknown[]) => void;
  canDo: (moveName: string, ...args: unknown[]) => true | string;
  connect: (gameId: string, matchID: string, playerID: string, credentials?: string) => void;
  disconnect: () => void;
}

const GAME_KEY: InjectionKey<GameContext> = Symbol('game-context');

export function provideGameContext(toast?: ToastContext): GameContext {
  const store = useGameStore();
  const { showToast } = toast ?? useToast();

  function canDo(moveName: string, ...args: unknown[]): true | string {
    const gId = store.gameId;
    if (!gId) return 'No game connected';

    const def = gameMap[gId];
    if (!def?.validateMove) return true;

    return def.validateMove(
      {
        G: store.G as BaseGameState,
        playerID: store.playerID ?? '',
        currentPlayer: store.currentPlayer,
      },
      moveName,
      ...args,
    );
  }

  function move(name: string, ...args: unknown[]) {
    const result = canDo(name, ...args);
    if (result !== true) {
      showToast(result, 'error');
      return;
    }

    const fn = store.moves[name];
    if (typeof fn === 'function') {
      (fn as (...a: unknown[]) => void)(...args);
    } else {
      showToast(
        `Move “${name}” is not available. Reconnect or refresh if the client is out of date with the server.`,
        'error',
      );
    }
  }

  const gameCtx: GameContext = {
    state: computed(() => store.G as BaseGameState),
    ctx: store.ctx as unknown as Ref<{ currentPlayer: string; phase?: string; gameover?: unknown }>,
    currentPlayer: computed({ get: () => store.currentPlayer, set() {} }),
    playerID: computed({ get: () => store.playerID, set() {} }),
    matchID: computed({ get: () => store.matchID, set() {} }),
    gameId: computed({ get: () => store.gameId, set() {} }),
    gameover: computed({ get: () => store.gameover, set() {} }),
    isActive: computed({ get: () => store.isActive, set() {} }),
    isConnected: computed({ get: () => store.isConnected, set() {} }),
    reconnecting: computed({ get: () => store.reconnecting, set() {} }),
    isMyTurn: computed(() => store.isMyTurn),
    canUndo: computed(() => store.canUndo),
    undo: () => store.undo(),
    move,
    canDo,
    connect: store.connect,
    disconnect: store.disconnect,
  };

  provide(GAME_KEY, gameCtx);
  return gameCtx;
}

export function useGame<S extends BaseGameState = BaseGameState>(): GameContext<S> {
  const injected = inject(GAME_KEY);
  if (!injected) {
    throw new Error('useGame() requires an ancestor component to call provideGameContext()');
  }
  return injected as unknown as GameContext<S>;
}
